/**
 * Similarity scoring for duplicate-incident detection. Used by the intake
 * banner and the merge modal. Purely local — no backend round-trip.
 */
import { Incident } from '../types/incident';

export interface DraftFields {
  type:        string;
  title:       string;
  location:    string;
  description: string;
}

function tokens(s: string): Set<string> {
  return new Set(
    (s || '').toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 3),
  );
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (!a.size || !b.size) return 0;
  let intersection = 0;
  a.forEach((t) => { if (b.has(t)) intersection += 1; });
  const union = a.size + b.size - intersection;
  return union ? intersection / union : 0;
}

/** Score 0-1. 1 == identical; ~0.45+ is the intake-banner threshold for
 *  "this is probably the same incident". */
export function scoreSimilarity(draft: DraftFields, candidate: Incident): number {
  const sameType = draft.type && draft.type === candidate.type ? 0.25 : 0;
  const titleSim = jaccard(tokens(draft.title), tokens(candidate.title));
  const locSim   = jaccard(tokens(draft.location), tokens(candidate.location));
  const descSim  = jaccard(
    tokens(`${draft.title} ${draft.description}`),
    tokens(`${candidate.title} ${candidate.description}`),
  );
  return sameType + 0.30 * titleSim + 0.20 * locSim + 0.25 * descSim;
}
